import { createContext, useEffect, useCallback, useRef, useReducer, useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { getPlatformAPI } from '../lib/platform'
import { showSuccessToast } from '../components/common/SuccessToast'

export type UpdateStatus = 'idle' | 'checking' | 'available' | 'downloading' | 'ready' | 'error'

export interface UpdateState {
  status: UpdateStatus
  newVersion: string | null
  currentVersion: string | null
  downloadPercent: number
  errorMessage: string | null
}

export const INITIAL_UPDATE_STATE: UpdateState = {
  status: 'idle',
  newVersion: null,
  currentVersion: null,
  downloadPercent: 0,
  errorMessage: null,
}

export type UpdateAction =
  | { type: 'IPC_CHECKING' }
  | { type: 'IPC_AVAILABLE'; version: string | null; currentVersion: string | null }
  | { type: 'IPC_PROGRESS'; percent: number }
  | { type: 'IPC_DOWNLOADED' }
  | { type: 'IPC_NOT_AVAILABLE' }
  | { type: 'IPC_ERROR'; message: string | null }
  | { type: 'DOWNLOAD_CLICKED' }
  | { type: 'DISMISS' }

/**
 * Pure state-transition function for update events. Side-effects (toast,
 * IPC invocations) live in the provider — this reducer is testable in
 * isolation without React or IPC.
 */
export function updateReducer(state: UpdateState, action: UpdateAction): UpdateState {
  switch (action.type) {
    case 'IPC_CHECKING':
      // Don't demote when the UI is already showing a state. A second check
      // fired while the dialog/overlay is open (e.g. user clicks "Updates
      // suchen" right after the 5s-after-launch auto-check) would otherwise
      // close the UI mid-exit-animation, and the follow-up `update-available`
      // would remount it next to the still-exiting one.
      if (
        state.status === 'available'
        || state.status === 'downloading'
        || state.status === 'ready'
        || state.status === 'error'
      ) {
        return state
      }
      return { ...state, status: 'checking' }
    case 'IPC_AVAILABLE':
      return {
        ...state,
        status: 'available',
        newVersion: action.version,
        currentVersion: action.currentVersion,
      }
    case 'IPC_PROGRESS':
      return { ...state, status: 'downloading', downloadPercent: action.percent }
    case 'IPC_DOWNLOADED':
      return { ...state, status: 'ready' }
    case 'IPC_NOT_AVAILABLE':
      return { ...state, status: 'idle' }
    case 'IPC_ERROR':
      return { ...state, status: 'error', errorMessage: action.message }
    case 'DOWNLOAD_CLICKED':
      // Clear any prior error so a retry click doesn't flash the error dialog
      // while main is working on the fresh download call.
      return state.status === 'error'
        ? { ...state, status: 'available', errorMessage: null }
        : state
    case 'DISMISS':
      return INITIAL_UPDATE_STATE
  }
}

interface UpdateStatusPayload {
  status: string
  data?: {
    version?: string
    currentVersion?: string
    percent?: number
    wasManual?: boolean
  } | string
}

export interface UpdateStatusContextValue {
  state: UpdateState
  download: () => void
  installAndRestart: () => void
  dismiss: () => void
  checkNow: () => void
}

export const UpdateStatusContext = createContext<UpdateStatusContextValue | undefined>(undefined)

export function UpdateStatusProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(updateReducer, INITIAL_UPDATE_STATE)
  const isElectronRef = useRef(getPlatformAPI().platform === 'electron')
  const { t } = useTranslation()

  // Ref mirror of `t` so the long-lived IPC listener can call the latest
  // translation function without re-subscribing on every render.
  const tRef = useRef(t)
  tRef.current = t

  useEffect(() => {
    if (!isElectronRef.current || !window.api?.receive) return

    let upToDateToastShown = false

    const cleanup = window.api.receive('update-status', (payload: UpdateStatusPayload) => {
      const data = typeof payload.data === 'object' && payload.data !== null ? payload.data : null

      switch (payload.status) {
        case 'checking-for-update':
          upToDateToastShown = false
          dispatch({ type: 'IPC_CHECKING' })
          break
        case 'update-available':
          dispatch({
            type: 'IPC_AVAILABLE',
            version: data?.version ?? null,
            currentVersion: data?.currentVersion ?? null,
          })
          break
        case 'download-progress':
          dispatch({ type: 'IPC_PROGRESS', percent: data?.percent ?? 0 })
          break
        case 'update-downloaded':
          dispatch({ type: 'IPC_DOWNLOADED' })
          break
        case 'update-not-available':
          // Silent for automatic (startup) checks. For manual checks the
          // main process tags the payload with `wasManual: true`.
          if (data?.wasManual === true && !upToDateToastShown) {
            upToDateToastShown = true
            const version = data.currentVersion ?? ''
            showSuccessToast({
              message: tRef.current('updater.currentVersionDetail', { version }),
            })
          }
          dispatch({ type: 'IPC_NOT_AVAILABLE' })
          break
        case 'update-error':
          dispatch({
            type: 'IPC_ERROR',
            message: typeof payload.data === 'string' ? payload.data : null,
          })
          break
      }
    })

    return cleanup
  }, [])

  const download = useCallback(() => {
    dispatch({ type: 'DOWNLOAD_CLICKED' })
    window.api?.invoke('download-update')
  }, [])

  const installAndRestart = useCallback(() => {
    window.api?.invoke('quit-and-install')
  }, [])

  const dismiss = useCallback(() => {
    dispatch({ type: 'DISMISS' })
  }, [])

  /**
   * Manually trigger an update check. The main-process IPC handler tags
   * this as a manual check so `update-not-available` comes back with
   * `wasManual: true`, triggering the up-to-date toast. Automatic
   * startup checks go through `appUpdater.checkForUpdates()` directly
   * without the flag and stay silent.
   */
  const checkNow = useCallback(() => {
    window.api?.invoke('check-for-updates')
  }, [])

  const value = useMemo<UpdateStatusContextValue>(
    () => ({ state, download, installAndRestart, dismiss, checkNow }),
    [state, download, installAndRestart, dismiss, checkNow],
  )

  return (
    <UpdateStatusContext.Provider value={value}>
      {children}
    </UpdateStatusContext.Provider>
  )
}
